import { useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  Form,
  Grid,
  Modal,
  SIZE,
  Stack,
  Text,
  toast,
  useForm,
} from '@quinyx/ui';
import * as z from 'zod';
import { useNavigate } from 'react-router-dom';

import { ICustomer } from '../../api/interfaces/customer';
import { useCreateCustomer } from '../../hooks/customer';
import { getSystemAdminToken } from '../../api/endpoints/support/system-admin-token';
import { CUSTOMERROUTE } from '../../layouts/customer';
import { countries, languages } from '../../config';
import {
  ENTITY_TYPE,
  ENTITY_TYPE_LABEL,
  getAllRequired,
} from '../../utils/copy/dependencyGraph';
import {
  backOfficeOf,
  copyTargetsFrom,
  gatewayOf,
  regionOfShard,
} from '../../utils/copy/environments';
import {
  ENVIRONMENT,
  Environment,
  getCurrentEnvironment,
} from '../../featureFlags';
import { useTranslate } from '../../hooks/translate';
import { useUser } from '../../context/authenticatedUser';

// A local back office talks to test's gateway, so for a copy it is test.
const CURRENT_ENV: Environment =
  getCurrentEnvironment() === ENVIRONMENT.LOCALHOST
    ? ENVIRONMENT.TEST
    : getCurrentEnvironment();

const ENVIRONMENT_LABEL_KEY = {
  [ENVIRONMENT.PROD]: 'copyTenant.environment.production',
  [ENVIRONMENT.RC]: 'copyTenant.environment.rc',
  [ENVIRONMENT.TEST]: 'copyTenant.environment.test',
  [ENVIRONMENT.PREPROD]: 'copyTenant.environment.preprod',
  [ENVIRONMENT.STAGING]: 'copyTenant.environment.staging',
  [ENVIRONMENT.QDAILY]: 'copyTenant.environment.qdaily',
  [ENVIRONMENT.LOCALHOST]: 'copyTenant.environment.localhost',
} as const satisfies Record<Environment, string>;

const schema = z.object({
  name: z.string().min(1),
  companyAlias: z.preprocess(
    (val) => (val === '' || val === undefined ? null : val),
    z.string().nullable().optional(),
  ),
  locale: z.string().min(1),
  country: z.string().min(1),
  timeZone: z.string().min(1),
  salesForceId: z.string().min(1),
  dateCreated: z.string().min(1),
  validUpto: z.string().min(1),
  useDistricts: z.boolean().optional(),
  localShiftTypes: z.boolean().optional(),
  shareWithinDistrict: z.boolean().optional(),
  active: z.boolean().optional(),
  targetEnv: z.string().min(1),
  adminToken: z.string().optional(),
});

type TFormValues = z.infer<typeof schema>;

interface CopyTenantModalProps {
  sourceDomainId: string;
  sourceCustomer: ICustomer;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

const today = new Date().toISOString().split('T')[0];

const CopyTenantModal = ({
  sourceDomainId,
  sourceCustomer,
  isOpen,
  onOpenChange,
}: CopyTenantModalProps) => {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { createCustomer, isLoading } = useCreateCustomer();
  // A copy stays within the region this back office is serving, which the admin's shard tells.
  const region = regionOfShard(useUser().shard_id);
  const targetEnvironments = copyTargetsFrom(CURRENT_ENV, region);
  const [agTemplatesChecked, setAgTemplatesChecked] = useState(true);
  // The source token is fetched before createCustomer starts, and its isLoading only covers the
  // latter, so the button would stay clickable while the token request runs - and a second click
  // would start a second copy.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // A copy into another environment is followed in that environment's back office: this one cannot
  // read its progress (CORS, VPN), so it says where the copy went and links there.
  const [startedIn, setStartedIn] = useState<{
    environment: Environment;
    progressUrl: string;
  } | null>(null);

  const form = useForm({
    schema,
    defaultValues: {
      name: t('copyTenant.defaultName', { name: sourceCustomer.name }),
      companyAlias: '',
      locale: sourceCustomer.locale,
      country: sourceCustomer.country,
      timeZone: sourceCustomer.timeZone,
      salesForceId: '',
      dateCreated: today,
      validUpto: sourceCustomer.validUpto,
      useDistricts: sourceCustomer.useDistricts,
      localShiftTypes: sourceCustomer.localShiftTypes,
      shareWithinDistrict: sourceCustomer.shareWithinDistrict,
      active: sourceCustomer.active,
      targetEnv: CURRENT_ENV,
      adminToken: '',
    },
  });

  const watchedEnv = form.watch('targetEnv');
  const targetEnvironment = targetEnvironments.find((e) => e === watchedEnv);
  const isCrossEnv = watchedEnv !== CURRENT_ENV;

  const timeZones: string[] = Intl.supportedValuesOf?.('timeZone') ?? [];

  const entityTypes = agTemplatesChecked
    ? getAllRequired([ENTITY_TYPE.AGREEMENT_TEMPLATES])
    : [];

  const handleSubmit = async (values: TFormValues) => {
    // Pasted, so it can carry whitespace the target environment would not accept as part of a token.
    const targetToken = values.adminToken?.trim();
    if (isCrossEnv && !targetToken) {
      form.setError('adminToken', {
        type: 'manual',
        message: t('copyTenant.targetTokenRequired'),
      });
      return;
    }

    if (entityTypes.length === 0) {
      toast({
        message: t('copyTenant.selectEntityType'),
        type: 'negative',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // A system admin token is only valid in the environment that issued it, so the copy needs
      // one for each end. This back office is pointed at the source environment, so that one can
      // be minted here; the target environment's has to be generated there and pasted in. Within
      // one environment the same token serves both.
      let sourceToken: string;
      try {
        sourceToken = await getSystemAdminToken();
      } catch {
        // Outside useCreateCustomer, so nothing else would tell the admin why nothing happened.
        toast({ message: t('copyTenant.sourceTokenFailed'), type: 'negative' });
        return;
      }
      const tokens = {
        source: sourceToken,
        target: isCrossEnv && targetToken ? targetToken : sourceToken,
      };

      const { id } = await createCustomer({
        customer: {
          name: values.name,
          companyAlias: values.companyAlias ?? undefined,
          locale: values.locale,
          // Combobox options are constrained to valid country codes from the countries config; zod validates string only
          country: values.country as ICustomer['country'],
          timeZone: values.timeZone,
          salesForceId: values.salesForceId,
          dateCreated: values.dateCreated,
          validUpto: values.validUpto,
          useDistricts: values.useDistricts ?? false,
          localShiftTypes: values.localShiftTypes ?? false,
          shareWithinDistrict: values.shareWithinDistrict ?? false,
          active: values.active ?? true,
          sourceDomainId: Number(sourceDomainId),
          entityTypes,
          // Tells the target environment's copy service where to read the source tenant from.
          sourceEnvironment: isCrossEnv ? CURRENT_ENV : undefined,
        },
        tokens,
        targetGateway:
          isCrossEnv && targetEnvironment && region
            ? gatewayOf(targetEnvironment, region)
            : undefined,
      });
      if (isCrossEnv && targetEnvironment) {
        setStartedIn({
          environment: targetEnvironment,
          progressUrl: `${backOfficeOf(targetEnvironment)}${CUSTOMERROUTE.IN_PROGRESS}/${id}`,
        });
        return;
      }
      onOpenChange(false);
      navigate(`${CUSTOMERROUTE.IN_PROGRESS}/${id}`);
    } catch {
      // Error toast handled by onError in useCreateCustomer
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeStarted = (open: boolean) => {
    if (!open) {
      setStartedIn(null);
    }
    onOpenChange(open);
  };

  if (startedIn) {
    return (
      <Modal open={isOpen} onOpenChange={closeStarted} size="s">
        <Modal.Header>
          <Modal.CloseButton
            ariaLabel="close-copy-tenant-modal"
            dataTestId="close-copy-tenant-modal"
          />
        </Modal.Header>
        <Modal.Content>
          <Modal.HeadlineText>{t('copyTenant.title')}</Modal.HeadlineText>
          <Stack spacing={SIZE.M} mt={SIZE.M}>
            <Text data-test-id="copy-tenant-started-elsewhere">
              {t('copyTenant.startedElsewhere', {
                environment: t(ENVIRONMENT_LABEL_KEY[startedIn.environment]),
              })}
            </Text>
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Button
            data-test-id="close-copy-tenant-started-button"
            text={t('common.close')}
            variant="text"
            onClick={() => closeStarted(false)}
          />
          <Button
            data-test-id="follow-copy-progress-button"
            text={t('copyTenant.followProgress')}
            onClick={() =>
              window.open(startedIn.progressUrl, '_blank', 'noopener')
            }
          />
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} onOpenChange={onOpenChange} size="xl">
      <Modal.Header>
        <Modal.CloseButton
          ariaLabel="close-copy-tenant-modal"
          dataTestId="close-copy-tenant-modal"
        />
      </Modal.Header>
      <Form methods={form} onSubmit={handleSubmit}>
        <Modal.Content>
          <Modal.HeadlineText>{t('copyTenant.title')}</Modal.HeadlineText>

          <Stack spacing={SIZE.M} mt={SIZE.M}>
            <Text variant="headlineHeavy">{t('copyTenant.tenantDetails')}</Text>

            <Grid columns={[1, 1, 1, 2]} gap={SIZE.M}>
              <Form.TextInput
                data-test-id="copy-tenant-name-input"
                name="name"
                label={t('customer.customerName')}
                required
              />
              <Form.TextInput
                data-test-id="copy-tenant-company-alias-input"
                name="companyAlias"
                label={t('customer.companyAlias')}
              />
              <Form.Combobox
                data-test-id="copy-tenant-language-select"
                name="locale"
                label={t('customer.language')}
                allowFiltering
                options={languages.map((l) => ({
                  label: l.name,
                  value: l.locale,
                }))}
                required
              />
              <Form.Combobox
                data-test-id="copy-tenant-country-select"
                name="country"
                label={t('customer.country')}
                allowFiltering
                options={Object.entries(countries).map(([key, value]) => ({
                  label: value,
                  value: key,
                }))}
                required
              />
              <Form.Combobox
                data-test-id="copy-tenant-timezone-select"
                name="timeZone"
                label={t('customer.timeZone')}
                allowFiltering
                options={timeZones.map((tz) => ({ label: tz, value: tz }))}
                required
              />
              <Form.TextInput
                data-test-id="copy-tenant-salesforce-id-input"
                name="salesForceId"
                label={t('customer.salesforceId')}
                required
              />
              <Form.TextInput
                type="date"
                data-test-id="copy-tenant-creation-date-picker"
                name="dateCreated"
                label={t('customer.creationDate')}
                required
              />
              <Form.TextInput
                type="date"
                data-test-id="copy-tenant-valid-upto-date-picker"
                name="validUpto"
                label={t('customer.validUpTo')}
                required
              />
            </Grid>

            <Stack spacing={SIZE.XS}>
              <Form.Checkbox
                data-test-id="copy-tenant-use-districts-checkbox"
                name="useDistricts"
                label={t('customer.useDistricts')}
              />
              <Form.Checkbox
                data-test-id="copy-tenant-local-shift-types-checkbox"
                name="localShiftTypes"
                label={t('customer.localShiftTypes')}
              />
              <Form.Checkbox
                data-test-id="copy-tenant-share-within-district-checkbox"
                name="shareWithinDistrict"
                label={t('customer.shareStaffWithinDistrict')}
              />
              <Form.Checkbox
                data-test-id="copy-tenant-is-active-checkbox"
                name="active"
                label={t('customer.isActive')}
              />
            </Stack>

            <Divider />

            <Text variant="headlineHeavy">
              {t('copyTenant.targetEnvironment')}
            </Text>

            <Grid columns={[1, 1, 2, 2]} gap={SIZE.M}>
              <Form.Combobox
                data-test-id="copy-tenant-environment-select"
                name="targetEnv"
                label={t('copyTenant.environment')}
                options={targetEnvironments.map((environment) => ({
                  label: t(ENVIRONMENT_LABEL_KEY[environment]),
                  value: environment,
                }))}
                required
              />
            </Grid>

            {isCrossEnv && (
              <Form.TextInput
                data-test-id="copy-tenant-admin-token-input"
                name="adminToken"
                label={t('admin.systemAdminToken.title')}
                info={t('copyTenant.targetTokenInfoFor', {
                  environment: targetEnvironment
                    ? t(ENVIRONMENT_LABEL_KEY[targetEnvironment])
                    : watchedEnv,
                })}
                required
              />
            )}

            <Divider />

            <Text variant="headlineHeavy">{t('copyTenant.dataToCopy')}</Text>

            <Stack spacing={SIZE.S}>
              <Checkbox
                data-test-id="copy-tenant-agreement-templates-checkbox"
                checked={agTemplatesChecked}
                onChange={(e) => setAgTemplatesChecked(e.target.checked)}
                label={t(ENTITY_TYPE_LABEL[ENTITY_TYPE.AGREEMENT_TEMPLATES])}
              />

              {agTemplatesChecked && (
                <Stack direction="row" spacing={SIZE.XS} pl={SIZE.M}>
                  <Divider orientation="vertical" flexItem />
                  <Stack spacing={SIZE.XS} p={SIZE.S}>
                    <Text variant="xSmallRegular" color="SurfaceOnSurfaceAlt">
                      {t('copyTenant.requiredDependencies')}
                    </Text>
                    <Checkbox
                      checked
                      readOnly
                      label={t(ENTITY_TYPE_LABEL[ENTITY_TYPE.SALARY_TYPES])}
                      data-test-id="copy-tenant-salary-types-checkbox"
                    />
                    <Checkbox
                      checked
                      readOnly
                      label={t(ENTITY_TYPE_LABEL[ENTITY_TYPE.ABSENCE_TYPES])}
                      data-test-id="copy-tenant-absence-types-checkbox"
                    />
                    <Stack pl={SIZE.M}>
                      <Checkbox
                        checked
                        readOnly
                        label={t(
                          ENTITY_TYPE_LABEL[ENTITY_TYPE.ABSENCE_REASON_TYPES],
                        )}
                        data-test-id="copy-tenant-absence-reason-types-checkbox"
                      />
                    </Stack>
                    <Checkbox
                      checked
                      readOnly
                      label={t(ENTITY_TYPE_LABEL[ENTITY_TYPE.TIME_TRACKERS])}
                      data-test-id="copy-tenant-time-trackers-checkbox"
                    />
                    <Checkbox
                      checked
                      readOnly
                      label={t(ENTITY_TYPE_LABEL[ENTITY_TYPE.OVERTIME_METHODS])}
                      data-test-id="copy-tenant-overtime-methods-checkbox"
                    />
                    <Checkbox
                      checked
                      readOnly
                      label={t(ENTITY_TYPE_LABEL[ENTITY_TYPE.BANK_HOLIDAYS])}
                      data-test-id="copy-tenant-bank-holidays-checkbox"
                    />
                  </Stack>
                </Stack>
              )}

              <Checkbox
                disabled
                label={t('copyTenant.districtAgreementTemplates')}
                info={t('copyTenant.requiresOrgHierarchy')}
                data-test-id="copy-tenant-district-agreement-templates-checkbox"
              />
              <Checkbox
                disabled
                label={t('customer.localShiftTypes')}
                info={t('copyTenant.requiresOrgHierarchy')}
                data-test-id="copy-tenant-local-shift-types-checkbox"
              />
              <Checkbox
                disabled
                label={t('copyTenant.globalShiftTypes')}
                info={t('copyTenant.comingSoon')}
                data-test-id="copy-tenant-global-shift-types-checkbox"
              />
              <Checkbox
                disabled
                label={t('copyTenant.shifts')}
                info={t('copyTenant.requiresEmployees')}
                data-test-id="copy-tenant-shifts-checkbox"
              />
              <Checkbox
                disabled
                label={t('copyTenant.absences')}
                info={t('copyTenant.requiresEmployees')}
                data-test-id="copy-tenant-absences-checkbox"
              />
              <Checkbox
                disabled
                label={t('copyTenant.timePunches')}
                info={t('copyTenant.requiresEmployees')}
                data-test-id="copy-tenant-time-punches-checkbox"
              />
            </Stack>
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <Button
            data-test-id="cancel-copy-tenant-button"
            text={t('common.cancel')}
            variant="text"
            onClick={() => onOpenChange(false)}
          />
          <Form.SubmitButton
            data-test-id="confirm-copy-tenant-button"
            text={t('copyTenant.title')}
            isLoading={isLoading || isSubmitting}
            disabled={!agTemplatesChecked}
          />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CopyTenantModal;
